import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { EReportStatus, EReportStatusSearchOrder } from '../types/report.types';
import { ESortDirection } from '../types/searchReports.types';

const parseStatuses = (param: string | null): EReportStatus[] => {
  if (!param) {
    return [];
  }
  return param
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is EReportStatus =>
      Object.values(EReportStatus).includes(s as EReportStatus)
    );
};

const parseProjects = (param: string | null): string[] => {
  if (!param) {
    return [];
  }
  return param
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

const readUrlState = (search: string) => {
  const searchParams = new URLSearchParams(search);
  const sortParam = searchParams.get('sortDirection');
  const sortDirection: ESortDirection =
    sortParam === ESortDirection.ASC
      ? ESortDirection.ASC
      : ESortDirection.DESC;
  const selectedStatuses = parseStatuses(searchParams.get('statuses'));
  const selectedProjects = parseProjects(searchParams.get('projects'));
  const createdAfter =
    searchParams.get('createdAfter') || searchParams.get('startDate') || undefined;
  const createdBefore =
    searchParams.get('createdBefore') || searchParams.get('endDate') || undefined;
  return {
    sortDirection,
    selectedStatuses,
    selectedProjects,
    createdAfter,
    createdBefore
  };
};

export const useReportsFilters = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sortDirection, setSortDirection] = useState<ESortDirection>(
    () => readUrlState(location.search).sortDirection
  );
  const [selectedStatuses, setSelectedStatuses] = useState<EReportStatus[]>(
    () => readUrlState(location.search).selectedStatuses
  );
  const [selectedProjects, setSelectedProjects] = useState<string[]>(
    () => readUrlState(location.search).selectedProjects
  );
  const [createdAfter, setCreatedAfter] = useState<string | undefined>(
    () => readUrlState(location.search).createdAfter
  );
  const [createdBefore, setCreatedBefore] = useState<string | undefined>(
    () => readUrlState(location.search).createdBefore
  );

  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  locationRef.current = location;
  navigateRef.current = navigate;

  useEffect(() => {
    const next = readUrlState(location.search);
    setSortDirection(next.sortDirection);
    setSelectedStatuses(
      EReportStatusSearchOrder.filter((status) =>
        next.selectedStatuses.includes(status)
      )
    );
    setCreatedAfter(next.createdAfter);
    setCreatedBefore(next.createdBefore);
    setSelectedProjects(next.selectedProjects);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(locationRef.current.search);
    queryParams.delete('startDate');
    queryParams.delete('endDate');
    if (sortDirection === ESortDirection.DESC) {
      queryParams.delete('sortDirection');
    } else {
      queryParams.set('sortDirection', sortDirection);
    }
    if (selectedStatuses.length > 0) {
      const statusesForQuery = EReportStatusSearchOrder.filter((status) =>
        selectedStatuses.includes(status)
      );
      queryParams.set('statuses', statusesForQuery.join(','));
    } else {
      queryParams.delete('statuses');
    }
    if (selectedProjects.length > 0) {
      queryParams.set('projects', selectedProjects.join(','));
    } else {
      queryParams.delete('projects');
    }
    if (createdAfter && createdBefore) {
      queryParams.set('createdAfter', createdAfter);
      queryParams.set('createdBefore', createdBefore);
    } else {
      queryParams.delete('createdAfter');
      queryParams.delete('createdBefore');
    }
    const searchString = queryParams.toString();
    const current = locationRef.current.search.startsWith('?')
      ? locationRef.current.search.slice(1)
      : locationRef.current.search;
    if (searchString === current) {
      return;
    }
    const newUrl = searchString
      ? `${locationRef.current.pathname}?${searchString}`
      : locationRef.current.pathname;
    navigateRef.current(newUrl, { replace: true });
  }, [sortDirection, selectedStatuses, selectedProjects, createdAfter, createdBefore]);

  const handleStatusChange = useCallback((value: string) => {
    setSelectedStatuses((previous) => {
      const status = value as EReportStatus;
      const next = previous.includes(status)
        ? previous.filter((item) => item !== status)
        : [...previous, status];
      return EReportStatusSearchOrder.filter((item) => next.includes(item));
    });
  }, []);

  const handleSortDirectionChange = useCallback((value: string) => {
    setSortDirection(
      value === ESortDirection.ASC ? ESortDirection.ASC : ESortDirection.DESC
    );
  }, []);

  const handleProjectChange = useCallback((publisherId: string) => {
    setSelectedProjects((previous) => {
      if (previous.includes(publisherId)) {
        return previous.filter((id) => id !== publisherId);
      }
      return [...previous, publisherId];
    });
  }, []);

  const handleCreatedRangeChange = useCallback(
    (nextCreatedAfter: string, nextCreatedBefore: string) => {
      if (!nextCreatedAfter || !nextCreatedBefore) {
        setCreatedAfter(undefined);
        setCreatedBefore(undefined);
        return;
      }
      setCreatedAfter(nextCreatedAfter);
      setCreatedBefore(nextCreatedBefore);
    },
    []
  );

  const clearFiltersButton = useMemo(() => {
    return (
      sortDirection !== ESortDirection.DESC ||
      selectedStatuses.length > 0 ||
      selectedProjects.length > 0 ||
      (createdAfter !== undefined && createdBefore !== undefined)
    );
  }, [sortDirection, selectedStatuses, selectedProjects, createdAfter, createdBefore]);

  const handleClearFilters = useCallback(() => {
    setSortDirection(ESortDirection.DESC);
    setSelectedStatuses([]);
    setSelectedProjects([]);
    setCreatedAfter(undefined);
    setCreatedBefore(undefined);
  }, []);

  return {
    sortDirection,
    selectedStatuses,
    selectedProjects,
    createdAfter,
    createdBefore,
    handleStatusChange,
    handleSortDirectionChange,
    handleProjectChange,
    handleCreatedRangeChange,
    clearFiltersButton,
    handleClearFilters
  };
};
